import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, User, Mail, Phone, Plus, UserCheck, Gift, Heart } from 'lucide-react';
import { useOffices } from '../components/offices/officesApi';
import { getSelectedOffice } from '../services/officeSelection';
import { OfficeSelect } from '../components/offices/OfficeSelect';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { ToastAction } from '../components/ui/toast';

const REFERRAL_SOURCE_TYPES = [
  "Dentist",
  "Patient",
  "Friend/Family",
  "Insurance",
  "Online/Website",
  "Other"
];

const AddPatientPage = () => {
  const navigate = useNavigate();
  const rt = new URLSearchParams(window.location.search).get('returnTo') || '/';
  const returnTo = rt.startsWith('/') && !rt.startsWith('//') ? rt : '/';
  const { user, practice } = useAuth();
  const { open: offices, multi: multiOffice } = useOffices();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('dentalToken');
  
  const [dentists, setDentists] = useState([]);
  const [loadingDentists, setLoadingDentists] = useState(true);
  
  // Inbound referral sources
  const [referralSources, setReferralSources] = useState([]);
  const [loadingReferralSources, setLoadingReferralSources] = useState(true);
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);
  const [newSourceData, setNewSourceData] = useState({
    name: '',
    sourceType: '',
    email: '',
    phone: ''
  });
  const [savingSource, setSavingSource] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredLanguage: '',
    primaryDentist: user?.firstName ? `Dr. ${user.firstName} ${user.lastName}` : '',
    // Inbound referral fields
    inboundReferralSourceType: '',
    inboundReferralSourceId: '',
    inboundReferralNotes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadDentists();
    loadReferralSources();
  }, []);

  const loadReferralSources = async () => {
    try {
      setLoadingReferralSources(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setReferralSources(data.sources || []);
    } catch (error) {
      console.error('Load referral sources error:', error);
    } finally {
      setLoadingReferralSources(false);
    }
  };

  // Filter referral sources by selected type
  const filteredReferralSources = formData.inboundReferralSourceType
    ? referralSources.filter(s => s.sourceType === formData.inboundReferralSourceType)
    : referralSources;

  const handleAddNewSource = async () => {
    if (!newSourceData.name.trim()) {
      toast({ title: "Error", description: "Name is required", variant: "destructive" });
      return;
    }
    if (!newSourceData.sourceType) {
      toast({ title: "Error", description: "Please select a referral type", variant: "destructive" });
      return;
    }

    try {
      setSavingSource(true);
      const response = await fetch(`${backendUrl}/api/inbound-referrals/sources`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSourceData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add referral source');
      }

      const data = await response.json();
      
      toast({ title: "Success", description: "Referral source added successfully" });
      
      // Add the new source to the list and select it
      await loadReferralSources();
      setFormData({
        ...formData,
        inboundReferralSourceType: newSourceData.sourceType,
        inboundReferralSourceId: data.sourceId
      });
      
      setShowAddSourceModal(false);
      setNewSourceData({ name: '', sourceType: '', email: '', phone: '' });
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSavingSource(false);
    }
  };

  const loadDentists = async () => {
    try {
      setLoadingDentists(true);
      
      // Try to load dentists from new API, fallback to old doctors API
      try {
        const dentistsResponse = await practiceApi.getDentists();
        setDentists(dentistsResponse.data || []);
      } catch (dentistsError) {
        console.warn('Failed to load dentists, trying doctors API:', dentistsError);
        // Fallback to old doctors API and convert format
        try {
          const doctorsResponse = await practiceApi.getPracticeDoctors();
          const doctorsData = doctorsResponse.data || [];
          // Convert doctors format to dentists format
          const convertedDentists = doctorsData.map(doctor => ({
            id: doctor.id,
            firstName: doctor.firstName || doctor.name?.split(' ')[0] || '',
            lastName: doctor.lastName || doctor.name?.split(' ').slice(1).join(' ') || '',
            email: doctor.email || '',
            phone: doctor.phone || '',
            specialties: doctor.specialties || []
          }));
          setDentists(convertedDentists);
        } catch (doctorsError) {
          console.warn('Failed to load doctors:', doctorsError);
          // Set default dentist from current user
          if (user?.firstName && user?.lastName) {
            setDentists([{
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email || '',
              phone: user.phone || '',
              specialties: []
            }]);
          }
        }
      }
      
    } catch (error) {
      console.error('Load dentists error:', error);
      toast({
        title: "Warning",
        description: "Could not load dentists. You can still add the patient.",
        variant: "destructive",
      });
    } finally {
      setLoadingDentists(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    // Email is optional, but if provided must be valid
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    // Phone is optional - no validation needed
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await practiceApi.createPatient({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        preferredLanguage: formData.preferredLanguage || null,
        primaryDentist: (formData.primaryDentist && formData.primaryDentist !== 'none') ? formData.primaryDentist : null,
        // Inbound referral data
        inboundReferralSourceId: formData.inboundReferralSourceId || null,
        inboundReferralSourceType: formData.inboundReferralSourceType || null,
        inboundReferralNotes: formData.inboundReferralNotes || null,
        officeId: formData.officeId || getSelectedOffice() || null
      });
      
      const patientName = `${formData.firstName} ${formData.lastName}`;
      const hasReferral = formData.inboundReferralSourceId;
      
      toast({
        title: "Success!",
        description: `Patient ${patientName} has been added successfully${hasReferral ? '. A thank you will be sent to the referral source!' : ''}.`,
        variant: "default",
        action: (
          <ToastAction
            altText="Assign Procedure"
            onClick={() => {
              // Navigate to assign procedure page with the new patient pre-selected
              navigate(`/assign-procedure?patientId=${response.data.patientId}&patientName=${encodeURIComponent(patientName)}`);
            }}
          >
            Assign Procedure
          </ToastAction>
        )
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        primaryDentist: user?.firstName ? `Dr. ${user.firstName} ${user.lastName}` : '',
        inboundReferralSourceType: '',
        inboundReferralSourceId: '',
        inboundReferralNotes: ''
      });
      
      // Navigate back to dashboard after delay
      // Note: If user clicks "Assign Procedure" in toast, they'll be navigated there before this timer fires
      setTimeout(() => {
        // Only navigate if we're still on the add patient page
        if (window.location.pathname === '/add-patient') {
          navigate(returnTo);
        }
      }, 4000);
      
    } catch (error) {
      console.error('Create patient error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add patient. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(returnTo)}
              className="flex items-center"
              data-testid="add-patient-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {returnTo === '/patient-management' ? 'Back to Manage Patients' : 'Back to Dashboard'}
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add New Patient</h1>
              <p className="text-gray-600">
                Add a patient to {practice?.name}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2 text-blue-600" />
              Patient Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                    disabled={loading}
                    className={errors.firstName ? 'border-red-500' : ''}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-500 mt-1">{errors.firstName}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                    disabled={loading}
                    className={errors.lastName ? 'border-red-500' : ''}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-500 mt-1">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address (Optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    disabled={loading}
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Phone Number (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                    disabled={loading}
                    className={`pl-10 ${errors.phone ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.phone && (
                  <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
                )}
              </div>

              {multiOffice && user?.role !== 'practice_staff' && (
                <div>
                  <Label htmlFor="officeId">Office</Label>
                  <OfficeSelect offices={offices} value={formData.officeId || getSelectedOffice() || offices.find((o) => o.isPrimary)?.id}
                    onChange={(v) => handleInputChange('officeId', v)} testId="add-patient-office-select" className="w-full mt-1" />
                </div>
              )}

              <div>
                <Label htmlFor="preferredLanguage">Preferred Language (Optional)</Label>
                <Select
                  value={formData.preferredLanguage}
                  onValueChange={(value) => handleInputChange('preferredLanguage', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="English (Default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="zh">Chinese (Simplified)</SelectItem>
                    <SelectItem value="vi">Vietnamese</SelectItem>
                    <SelectItem value="ko">Korean</SelectItem>
                    <SelectItem value="pt">Portuguese</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="ar">Arabic</SelectItem>
                    <SelectItem value="ru">Russian</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="it">Italian</SelectItem>
                    <SelectItem value="pl">Polish</SelectItem>
                    <SelectItem value="tl">Tagalog (Filipino)</SelectItem>
                    <SelectItem value="he">Hebrew</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="primaryDentist">Primary Dentist (Optional)</Label>
                {loadingDentists ? (
                  <div className="flex items-center space-x-2 p-3 border rounded-md">
                    <LoadingSpinner size="sm" />
                    <span className="text-sm text-gray-500">Loading dentists...</span>
                  </div>
                ) : (
                  <Select
                    value={formData.primaryDentist}
                    onValueChange={(value) => handleInputChange('primaryDentist', value)}
                  >
                    <SelectTrigger className={errors.primaryDentist ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select a primary dentist" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex items-center text-gray-500">
                          <UserCheck className="h-4 w-4 mr-2" />
                          No primary dentist assigned
                        </div>
                      </SelectItem>
                      {dentists.length > 0 ? (
                        dentists.map((dentist) => (
                          <SelectItem key={dentist.id} value={`Dr. ${dentist.firstName} ${dentist.lastName}`}>
                            <div className="flex items-center">
                              <UserCheck className="h-4 w-4 mr-2 text-blue-600" />
                              Dr. {dentist.firstName} {dentist.lastName}
                              {dentist.specialties && dentist.specialties.length > 0 && (
                                <span className="ml-2 text-xs text-gray-500">
                                  ({dentist.specialties.slice(0, 2).join(', ')})
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-dentists" disabled>
                          <div className="flex items-center text-gray-400">
                            <UserCheck className="h-4 w-4 mr-2" />
                            No dentists found - Add dentists in Practice Settings
                          </div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
                {errors.primaryDentist && (
                  <p className="text-sm text-red-500 mt-1">{errors.primaryDentist}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  The primary dentist will be pre-selected when assigning procedures to this patient
                </p>
              </div>

              {/* Inbound Referral Section */}
              <div className="border-t pt-6 mt-6">
                <div className="flex items-center mb-4">
                  <Gift className="h-5 w-5 text-green-600 mr-2" />
                  <h3 className="font-medium text-gray-900">Inbound Referral (Optional)</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  If this patient was referred by someone, select the referral type and source. An automatic thank you will be sent!
                </p>

                <div className="space-y-4">
                  {/* Step 1: Select Referral Type */}
                  <div>
                    <Label htmlFor="referralType">Referral Type</Label>
                    <Select
                      value={formData.inboundReferralSourceType}
                      onValueChange={(value) => {
                        setFormData({
                          ...formData,
                          inboundReferralSourceType: value,
                          inboundReferralSourceId: '' // Reset source when type changes
                        });
                      }}
                    >
                      <SelectTrigger data-testid="referral-type-select">
                        <SelectValue placeholder="Select referral type (if applicable)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="text-gray-500">No referral</span>
                        </SelectItem>
                        {REFERRAL_SOURCE_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Step 2: Select or Add Referral Source */}
                  {formData.inboundReferralSourceType && formData.inboundReferralSourceType !== 'none' && (
                    <div>
                      <Label htmlFor="referralSource">Referral Source</Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          {loadingReferralSources ? (
                            <div className="flex items-center space-x-2 p-3 border rounded-md">
                              <LoadingSpinner size="sm" />
                              <span className="text-sm text-gray-500">Loading sources...</span>
                            </div>
                          ) : (
                            <Select
                              value={formData.inboundReferralSourceId}
                              onValueChange={(value) => setFormData({...formData, inboundReferralSourceId: value})}
                            >
                              <SelectTrigger data-testid="referral-source-select">
                                <SelectValue placeholder="Select referral source" />
                              </SelectTrigger>
                              <SelectContent>
                                {filteredReferralSources.length > 0 ? (
                                  filteredReferralSources.map((source) => (
                                    <SelectItem key={source.id} value={source.id}>
                                      <div className="flex items-center">
                                        <Heart className="h-4 w-4 mr-2 text-pink-500" />
                                        {source.name}
                                        {source.totalReferrals > 0 && (
                                          <span className="ml-2 text-xs text-gray-500">
                                            ({source.totalReferrals} referral{source.totalReferrals !== 1 ? 's' : ''})
                                          </span>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="no-sources" disabled>
                                    <span className="text-gray-500">No {formData.inboundReferralSourceType.toLowerCase()} sources yet</span>
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setNewSourceData({
                              name: '',
                              sourceType: formData.inboundReferralSourceType,
                              email: '',
                              phone: ''
                            });
                            setShowAddSourceModal(true);
                          }}
                          data-testid="add-new-source-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          New
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Notes about the referral */}
                  {formData.inboundReferralSourceId && (
                    <div>
                      <Label htmlFor="referralNotes">Referral Notes (Optional)</Label>
                      <textarea
                        id="referralNotes"
                        value={formData.inboundReferralNotes}
                        onChange={(e) => setFormData({...formData, inboundReferralNotes: e.target.value})}
                        placeholder="Any additional notes about this referral..."
                        className="w-full p-2 border rounded-md text-sm"
                        rows={2}
                        data-testid="referral-notes-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(returnTo)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Adding Patient...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Patient
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Add New Referral Source Modal */}
      <Dialog open={showAddSourceModal} onOpenChange={setShowAddSourceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Referral Source</DialogTitle>
            <DialogDescription>
              Add a new {formData.inboundReferralSourceType?.toLowerCase() || 'referral source'} who referred this patient.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newSourceData.name}
                onChange={(e) => setNewSourceData({...newSourceData, name: e.target.value})}
                placeholder="e.g., Dr. John Smith, Jane Doe"
                data-testid="new-source-name-input"
              />
            </div>
            <div>
              <Label>Referral Type</Label>
              <Select 
                value={newSourceData.sourceType} 
                onValueChange={(v) => setNewSourceData({...newSourceData, sourceType: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_SOURCE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email (for thank you email)</Label>
              <Input
                type="email"
                value={newSourceData.email}
                onChange={(e) => setNewSourceData({...newSourceData, email: e.target.value})}
                placeholder="email@example.com"
                data-testid="new-source-email-input"
              />
            </div>
            <div>
              <Label>Phone (for thank you text)</Label>
              <Input
                value={newSourceData.phone}
                onChange={(e) => setNewSourceData({...newSourceData, phone: e.target.value})}
                placeholder="(555) 123-4567"
                data-testid="new-source-phone-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSourceModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNewSource} disabled={savingSource} data-testid="save-new-source-btn">
              {savingSource ? 'Saving...' : 'Add & Select'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddPatientPage;